import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IconChevronRight, IconFlame, IconPin, IconSparkles } from "@tabler/icons-react";
import { useTranslation } from "../../i18n/hooks/useTranslation";

// Content of the AI-templates mega panel (PageMenu portals it into the navbar's full-width slot and
// narrows the shell to this content — see AI_SHELL_CLASS there).
//
// Two panes side by side, kept compact. LEFT: every published category as a row, in 2 columns
// ("Trending" on top, spanning both) — categories are the primary navigation, so none hides behind a
// "see more"; a very long list scrolls inside its own box instead of making the panel taller.
// RIGHT: the hovered category's most-used templates as big image cards (the picture sells the template; the
// usage count sits on its bottom edge and the title only appears there on hover), 3 columns × 2 rows. "Trending" is the default so
// the panel is useful before any hover.

const PREVIEW_COUNT = 6; // top 6 templates = 2 rows × 3 columns

// Hover intent. The preview follows the pointer, but with two category columns the pointer often has
// to cross the second column on its way to the previews — so a category only takes over once the
// pointer has slowed down on it (moved less than INTENT_PX within INTENT_MS), not on first contact.
const INTENT_MS = 60;
const INTENT_PX = 6;

// Data-driven per-category accent (categories have no colour field) — same idea as
// the Tools megamenu, which tints each category with its own colour.
const PALETTE = [
    { color: "#E68A00", bg: "#FFF4E0" },
    { color: "#3B82F6", bg: "#EAF2FF" },
    { color: "#10B981", bg: "#E8F8F1" },
    { color: "#8B5CF6", bg: "#F1EBFF" },
    { color: "#F43F5E", bg: "#FFECEF" },
    { color: "#14B8A6", bg: "#E6FAF7" },
];
const TRENDING_ACCENT = { color: "#F97316", bg: "#FFF1E6" };

const formatUses = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const byPopularity = (a, b) =>
    ((b.trendingCount || 0) - (a.trendingCount || 0)) ||
    ((b.usageCount || 0) - (a.usageCount || 0)) ||
    (new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

function CategoryItem({ active, accent, isDark, icon, label, count, to, wide, onEnter, onMove, onLeave, onFocus, onClose }) {
    return (
        <Link
            to={to}
            onClick={onClose}
            onMouseEnter={onEnter}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            onFocus={onFocus}
            title={label}
            className={`flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 no-underline transition-colors duration-100 ${wide ? "col-span-2" : ""} ${active ? "" : "border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800/70"}`}
            style={
                active
                    ? {
                        backgroundColor: isDark ? `${accent.color}28` : accent.bg,
                        borderColor: `${accent.color}${isDark ? "70" : "55"}`,
                        color: accent.color,
                    }
                    : undefined
            }
        >
            <span className={`shrink-0 ${active ? "" : "text-zinc-400 dark:text-zinc-500"}`} style={active ? { color: accent.color } : undefined}>
                {icon}
            </span>
            <span className={`min-w-0 flex-1 truncate text-[13px] font-semibold leading-tight ${active ? "" : "text-zinc-700 dark:text-zinc-300"}`}>{label}</span>
            {count != null && (
                <span
                    className={`shrink-0 text-[11px] font-semibold tabular-nums ${active ? "" : "text-zinc-500 dark:text-zinc-400"}`}
                    style={{ opacity: active ? 0.85 : 0.6 }}
                >
                    {count}
                </span>
            )}
        </Link>
    );
}

function PreviewCard({ tpl, isBn, usesLabel, onClose }) {
    const name = isBn && tpl.nameBn ? tpl.nameBn : tpl.name;
    return (
        <Link
            to={`/ai-templates/${tpl.slug}`}
            onClick={onClose}
            title={name}
            className="group relative block aspect-[4/5] min-w-0 overflow-hidden rounded-lg border border-zinc-200/80 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 no-underline transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
        >
            {tpl.thumbnail ? (
                <img
                    src={tpl.thumbnail}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                    <IconSparkles size={28} className="text-zinc-300 dark:text-zinc-600" />
                </span>
            )}
            {/* Bottom edge of the picture: the usage count is always visible; the title slides in on hover/focus only. */}
            <span className={`absolute inset-x-0 bottom-0 flex flex-col gap-0.5 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-2 pb-1.5 pt-8 transition-opacity duration-150 ${tpl.usageCount > 0 ? "" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"}`}>
                <span className="line-clamp-2 max-h-0 overflow-hidden text-[11.5px] font-semibold leading-tight text-white opacity-0 transition-all duration-150 group-hover:max-h-10 group-hover:opacity-100 group-focus-visible:max-h-10 group-focus-visible:opacity-100">{name}</span>
                {tpl.usageCount > 0 && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-[10.5px] font-medium text-white/90">
                        <IconFlame size={11} className="text-orange-400" />
                        {formatUses(tpl.usageCount)} {usesLabel}
                    </span>
                )}
            </span>
        </Link>
    );
}

function Skeleton() {
    return (
        <div className="grid grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4">
            <div className="grid grid-cols-2 content-start gap-0.5">
                {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className={`h-[30px] rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse ${i === 0 ? "col-span-2" : ""}`} />
                ))}
            </div>
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-3">
                <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: PREVIEW_COUNT }).map((_, i) => (
                        <div key={i} className="aspect-[4/5] rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function AiTemplateMegaMenu({ categories, templates, loading, isDark, onClose }) {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === "bn";
    const usesLabel = t("worldCupJersey.uses");
    const [activeKey, setActiveKey] = useState("trending");

    // One entry per PUBLISHED category (the caller already filters to published). Categories with
    // templates come first (admin-pinned first, then biggest), each with its templates pre-sorted by
    // popularity; categories that have none yet follow in the admin's sort order and show a
    // "coming soon" preview. "Trending" is a virtual first row.
    const { entries, trending } = useMemo(() => {
        const published = templates.filter((tp) => tp.slug);
        const grouped = new Map();
        for (const tp of published) {
            if (tp.categoryId == null) continue;
            if (!grouped.has(tp.categoryId)) grouped.set(tp.categoryId, []);
            grouped.get(tp.categoryId).push(tp);
        }
        const all = categories.map((cat) => ({ cat, list: (grouped.get(cat.id) || []).sort(byPopularity) }));
        const filled = all
            .filter((x) => x.list.length > 0)
            .sort((a, b) => (Number(!!b.cat.pinned) - Number(!!a.cat.pinned)) || (b.list.length - a.list.length));
        const empty = all
            .filter((x) => x.list.length === 0)
            .sort((a, b) => ((a.cat.sortOrder ?? 0) - (b.cat.sortOrder ?? 0)) || String(a.cat.name).localeCompare(String(b.cat.name)));
        const list = [...filled, ...empty];
        return { entries: list, trending: [...published].sort(byPopularity).slice(0, PREVIEW_COUNT) };
    }, [categories, templates]);

    // Hover intent (see INTENT_MS): the pending switch is polled while the pointer is on a row and
    // dropped the moment it leaves, so crossing a row at speed never changes the preview.
    const intent = useRef({ timer: null, x: 0, y: 0, px: 0, py: 0 });
    useEffect(() => () => clearInterval(intent.current.timer), []);
    const stopIntent = () => {
        clearInterval(intent.current.timer);
        intent.current.timer = null;
    };
    const startIntent = (key) => (e) => {
        stopIntent();
        if (String(key) === String(activeKey)) return;
        const s = intent.current;
        s.x = s.px = e.clientX;
        s.y = s.py = e.clientY;
        s.timer = setInterval(() => {
            if (Math.abs(s.x - s.px) + Math.abs(s.y - s.py) < INTENT_PX) {
                stopIntent();
                setActiveKey(key);
            } else {
                s.px = s.x;
                s.py = s.y;
            }
        }, INTENT_MS);
    };
    const trackIntent = (e) => {
        intent.current.x = e.clientX;
        intent.current.y = e.clientY;
    };

    const catName = (c) => (isBn && c.nameBn ? c.nameBn : c.name);
    const active = entries.find((e) => String(e.cat.id) === String(activeKey));
    const isTrending = !active;
    const accentOf = (idx) => PALETTE[idx % PALETTE.length];
    const activeAccent = isTrending ? TRENDING_ACCENT : accentOf(entries.indexOf(active));
    const previewList = isTrending ? trending : active.list.slice(0, PREVIEW_COUNT);
    const previewTo = isTrending ? "/ai-templates" : `/ai-templates?category=${active.cat.slug}`;
    const previewTitle = isTrending ? t("aiTemplateMenu.trending") : catName(active.cat);
    // Categories arrive before the templates: until the list is in, show placeholders rather than
    // a misleading "coming soon" / empty pane.
    const stillLoading = loading && templates.length === 0;
    const isEmptyCategory = !stillLoading && !isTrending && active.list.length === 0;
    const previewSub = isTrending ? t("aiTemplateMenu.thisWeek") : stillLoading ? "" : isEmptyCategory ? t("aiTemplateMenu.comingSoon") : `${active.list.length} ${t("aiTemplateMenu.templates")}`;
    const hasMoreInActive = !isTrending && active.list.length > PREVIEW_COUNT;

    // Rows in display order: [trending, ...categories]
    const items = [
        { key: "trending", wide: true, accent: TRENDING_ACCENT, icon: <IconFlame size={15} />, label: t("aiTemplateMenu.trending"), count: null, to: "/ai-templates" },
        ...entries.map(({ cat, list }, i) => ({
            key: cat.id,
            accent: accentOf(i),
            icon: cat.pinned ? <IconPin size={14} /> : <IconSparkles size={14} />,
            label: catName(cat),
            count: list.length || null, // no badge on a category with nothing in it yet
            to: `/ai-templates?category=${cat.slug}`,
        })),
    ];
    const shownKey = isTrending ? "trending" : activeKey;

    if (loading && entries.length === 0) return <Skeleton />;
    if (entries.length === 0 && trending.length === 0) {
        return <div className="py-8 text-center text-sm text-zinc-400">{t("aiTemplateMenu.empty")}</div>;
    }

    return (
        <div className="grid grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4">
            {/* LEFT — every category, 2 columns, no box of its own so a short list leaves no empty card */}
            <div className="min-w-0">
                <div className="flex items-center justify-between gap-2 px-1 pb-1.5">
                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t("aiTemplateMenu.categories")}</span>
                    <Link
                        to="/ai-templates"
                        onClick={onClose}
                        className="inline-flex items-center gap-0.5 text-xs font-semibold no-underline text-primary hover:opacity-80"
                    >
                        <span className="text-xs">{t("aiTemplateMenu.viewAll")}</span>
                        <IconChevronRight size={12} />
                    </Link>
                </div>
                <div className="max-h-[400px] overflow-y-auto pr-1">
                    <div className="grid grid-cols-2 content-start gap-0.5">
                        {items.map((c) => (
                            <CategoryItem
                                key={c.key}
                                active={String(c.key) === String(shownKey)}
                                accent={c.accent}
                                isDark={isDark}
                                icon={c.icon}
                                label={c.label}
                                count={c.count}
                                to={c.to}
                                wide={c.wide}
                                onEnter={startIntent(c.key)}
                                onMove={trackIntent}
                                onLeave={stopIntent}
                                onFocus={() => { stopIntent(); setActiveKey(c.key); }}
                                onClose={onClose}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* RIGHT — top templates of the hovered row, 2 columns */}
            <div
                className="min-w-0 rounded-xl p-3"
                style={{
                    backgroundColor: isDark ? `${activeAccent.color}10` : `${activeAccent.bg}80`,
                    border: `1.5px solid ${activeAccent.color}${isDark ? "30" : "20"}`,
                }}
            >
                <div className="mb-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="truncate text-[15px] font-bold leading-tight text-zinc-900 dark:text-zinc-100">{previewTitle}</div>
                        <div className="mt-0.5 min-h-[16px] text-xs font-medium" style={{ color: activeAccent.color }}>{previewSub}</div>
                    </div>
                    <Link
                        to={previewTo}
                        onClick={onClose}
                        className="shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold no-underline hover:opacity-80"
                        style={{ color: activeAccent.color }}
                    >
                        {hasMoreInActive
                            ? t("aiTemplateMenu.viewAllIn", { n: active.list.length })
                            : t("aiTemplateMenu.viewCategory")}
                        <IconChevronRight size={14} />
                    </Link>
                </div>
                <div className="relative grid grid-cols-3 gap-2">
                    {isEmptyCategory && (
                        <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-1.5 text-center">
                            <IconSparkles size={22} style={{ color: activeAccent.color }} />
                            <span className="block text-sm font-semibold text-zinc-700 dark:text-zinc-200">{t("aiTemplateMenu.comingSoonMsg")}</span>
                        </div>
                    )}
                    {stillLoading
                        ? Array.from({ length: PREVIEW_COUNT }).map((_, i) => (
                            <div key={`sk-${i}`} className="aspect-[4/5] rounded-lg bg-white/70 dark:bg-zinc-800 animate-pulse" />
                        ))
                        : previewList.map((tp) => (
                            <PreviewCard key={tp.id} tpl={tp} isBn={isBn} usesLabel={usesLabel} onClose={onClose} />
                        ))}
                    {/* Invisible fillers keep the grid at its full height for every row, so the
                        panel doesn't grow/shrink as the pointer moves across categories with
                        fewer templates. */}
                    {!stillLoading && Array.from({ length: Math.max(0, PREVIEW_COUNT - previewList.length) }).map((_, k) => (
                        <div key={`ph-${k}`} aria-hidden="true" className="invisible aspect-[4/5]" />
                    ))}
                </div>
            </div>
        </div>
    );
}
